import React, { useState } from 'react';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import EventDates from '../CalendarEvents/inputs/EventDates';
import { getToday } from '@shared/services/dateService';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import _ from 'lodash';
import ConfirmDialog from '@shared/components/confirmDialog/ConfirmDialog';
import RequestDateInputValidator from '../../validators/RequestDateInputValidator';
import {
  CHANGE_DATE_REQUEST_STATUS,
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_LENGTH
} from '../../constants/ChangeDateRequestConstants';
import FormProviderWrapper from '@shared/components/confirmDialog/dialogWrappers/FormProviderWrapper';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';

/**
 * @typedef {object} ChangeDateRequestProps
 * @property {boolean} [loading]
 * @property {Array<*>} [excludedDateIntervals]
 * @property {number} [status]
 * @property {(requestData: { startDate: Date | null, endDate: Date | null, comment: string }) => void | Promise<void>} onSubmit
 */

/**
 * @param {ChangeDateRequestProps} props
 */
const ChangeDateRequest = props => {
  const { onSubmit, loading = false, excludedDateIntervals = [], status = null } = props;

  const [openModal, setOpenModal] = useState(false);

  const buttonSetting = buttonSettings[status];

  const form = useForm({
    resolver: joiResolver(RequestDateInputValidator.schema),
    defaultValues: defaultRequestData
  });

  const {
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
    reset,
    control
  } = form;

  const disableConfirmBtn = form.getValues(['startDate', 'endDate']).some(date => !date);

  const handleConfirm = async requestData => {
    await onSubmit(requestData);
    setOpenModal(false);
    reset();
  };

  const getOnDateRangeChangedHandler = (startDate, endDate) => {
    setValue('startDate', startDate);
    setValue('endDate', endDate);
  };

  const handleOnButtonClick = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    reset();
  };

  const dialogContent = (
    <div className='date-request-dialog-content'>
      <EventDates
        selectedStartDate={watch('startDate')}
        selectedEndDate={watch('endDate')}
        onDateRangeChanged={getOnDateRangeChangedHandler}
        excludedDateIntervals={excludedDateIntervals}
        minDate={getToday()}
        label='Event Dates'
        name='eventDates'
        placeholder='Select date'
      />

      <div className='input-area'>
        <div className='label bold'>Comments*</div>
        <Controller
          control={control}
          name='comment'
          render={({ field }) => (
            <MuiInputTextWithRemainingChars
              className='value'
              error={hasError(errors, 'comment')}
              disableUnderline
              multiline={true}
              rows={5}
              value={field.value}
              onChange={field.onChange}
              minLength={COMMENT_MIN_LENGTH}
              maxLength={COMMENT_MAX_LENGTH}
              hideRemainingChars={false}
              placeholder={commentInputPlaceholder}
            />
          )}
        />
      </div>
    </div>
  );

  return (
    <SkeletonWrapper show={loading} inheritChildHeight>
      <div>
        <Button
          className='primary-button lg'
          onClick={handleOnButtonClick}
          disabled={buttonSetting.disabled}>
          {buttonSetting.text}
        </Button>

        <FormProviderWrapper form={form}>
          <ConfirmDialog
            dialogClassName='date-request-dialog'
            open={openModal}
            handleClose={handleClose}
            dialogContent={dialogContent}
            handleOnConfirm={handleSubmit(handleConfirm)}
            confirmBtnText='Submit'
            cancelBtnText='Cancel'
            disableConfirmBtn={disableConfirmBtn}
            errorMessage={errors?.comment?.message || ''}
            dialogMode={DIALOG_MODES.INTERNAL_FORM}
          />
        </FormProviderWrapper>
      </div>
    </SkeletonWrapper>
  );
};

const hasError = (errors, fieldName) => Boolean(_.get(errors, fieldName));

const commentInputPlaceholder = 'Describe requested changes for the Appraisal Date(s).';

const defaultRequestData = {
  startDate: null,
  endDate: null,
  comment: ''
};

const pendingApprovalSettings = { text: 'Change Request submitted', disabled: true };
const buttonSettings = {
  [CHANGE_DATE_REQUEST_STATUS.PENDING_ADMIN_APPROVAL]: pendingApprovalSettings,
  [CHANGE_DATE_REQUEST_STATUS.PENDING_FDA_APPROVAL]: pendingApprovalSettings
};

buttonSettings[null] = { text: 'Request Date Change', disabled: false };

export default ChangeDateRequest;
